<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import DataLayer from '@services/data-layer/index'
import { useQueueStore } from '@state/stores/queue'
import { SOCIAL_SHARE_IMAGE, usePageHead } from '@composables/use-page-head'
import { useRoute } from 'vue-router'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useQueueService } from '@composables/use-queue-service'
import Layout from '@layouts/main-layout'
import PlaylistsTableBlock from '@components/playlists/playlists-table-block'
import PlaylistInfo from '@components/playlists/playlist-info'
import modalService from '@/services/modal-service'

const PAGE_SIZE = 20
const PLACEHOLDER_ROWS = 20
const LOAD_MORE_THROTTLE_MS = 200
const BOTTOM_OFFSET_RATIO = 3

const route = useRoute()
const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const queueStore = useQueueStore()
const queueService = useQueueService()

const chart = ref(null)

const chartMixes = ref(
    Array.from(Array(PLACEHOLDER_ROWS), (_mix, i) => ({
        id: Number.MAX_SAFE_INTEGER - i,
        genre: [],
    }))
)
const shouldTransition = ref(false)
const maxMixes = ref(PAGE_SIZE)
const totalMixes = ref(null)
const shouldLoadMore = ref(false)
const loaded = ref(false)

const id = computed(() => route.params.id)

const scroll = computed(() => appearanceStore.scroll)

const loggedIn = computed(() => authStore.loggedIn)

const currentPosition = computed(() => queueStore.position)

const hasReachedMaxMixes = computed(() => maxMixes.value >= Number(totalMixes.value))

function loadMoreMixes(fillAll = false) {
    loaded.value = false
    maxMixes.value += PAGE_SIZE
    const wanted = fillAll ? totalMixes.value - chartMixes.value.length : PAGE_SIZE

    return DataLayer.playlists
        .getRevisionsMixes(id.value, wanted, chartMixes.value.length)
        .then(mixes => {
            loaded.value = true
            return chartMixes.value.push(...mixes.body.map(revision => revision.mixes))
        })
}

function playPlaylist(position) {
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }

    const play = () =>
        queueService.playMixMultiple({
            mixes: [...chartMixes.value],
            id: chart.value.id,
            position,
        })

    // Playing the chart queues all of it, so anything not paged in yet has to be fetched first.
    if (chartMixes.value.length === totalMixes.value) {
        play()
    } else {
        loadMoreMixes(true).then(play)
    }
}

watch(scroll, () => {
    if (hasReachedMaxMixes.value) return

    shouldLoadMore.value = true

    const bottomOfWindow =
        scroll.value + window.innerHeight >=
        appearanceStore.scrollChild.offsetHeight - window.innerHeight / BOTTOM_OFFSET_RATIO

    if (bottomOfWindow && shouldLoadMore.value && loaded.value) {
        shouldLoadMore.value = false
        setTimeout(() => {
            shouldLoadMore.value = true
        }, LOAD_MORE_THROTTLE_MS)
        loadMoreMixes()
    }
})

onMounted(() => {
    DataLayer.playlists.getById(id.value).then(res => {
        chart.value = res.body
    })

    DataLayer.playlists.getRevisionsMixes(id.value, PAGE_SIZE, 0).then(res => {
        loaded.value = true
        totalMixes.value = Number(res.headers['x-items-total'])
        chartMixes.value = res.body.map(revision => revision.mixes)
    })
})

usePageHead(computed(() => ({
    title: chart.value ? `${chart.value.revisions_title} - Chart` : 'Chart',
    description: chart.value?.revisions_description || 'A chart on FRISKY',
    image: chart.value?.revisions_album_art?.url || SOCIAL_SHARE_IMAGE,
    type: 'music.playlist',
})))
</script>

<template>
    <Layout>
        <BaseContent>
            <div class="container">
                <BaseSection padded>
                    <h6 class="title">Chart</h6>
                    <div class="content">
                        <div class="details">
                            <PlaylistInfo
                                :playlist-mixes="chartMixes"
                                :playlist="chart"
                                @play-playlist="playPlaylist(currentPosition !== -1 ? currentPosition : 0)"
                            />
                        </div>
                        <PlaylistsTableBlock
                            :mixes="chartMixes"
                            :error="{
                                title: `There are no mixes in this chart yet`,
                            }"
                            :should-transition="shouldTransition"
                            playlist-id
                            genres
                            is-indexed-list
                            @play-playlist-mix="playPlaylist"
                        />
                    </div>
                </BaseSection>
            </div>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.container {
    margin-top: $s-h;
}

.content {
    @include widescreen {
        display: grid;
        grid-template-columns: 1fr 3fr;
        gap: $s-xxxl;
    }
}

.details {
    max-width: 300px;
    margin: 0 auto;
}

.title {
    margin: 0;

    @include h6;

    @include desktop {
        @include h5;
    }
}
</style>
